<template>
    <ResizableContent
        class="conversations"
        :handles="['mr']"
        :onResize="onResizeConversations"
        :max-width="maxGridConversationsWidth"
        :min-width="minGridConversationsWidth"
        :width="width"
    >
        <GridContactListToolBar />
        <div class="conversation-lists" ref="conversationsList" @scroll="scrollListener">
            <GridContactListItem
                v-for="c of contacts"
                :contact="c"
                :key="`${c._id}-${selectedSortField}`"
                :ref="`contact-${c._id}`"
                :activeContact="contact"
                :currentTime="currentTime"
                @joinConversation="joinConversation(c._id)"
                @show-context-menu="onShowContextMenu"
            />
            <div class="empty-conversations" v-if="contacts.length <= 0">
                <div class="empty-conversations-content">
                    <EmptyChatList />
                    <div class="empty-conversations-content-title">{{ $t("chat.gridContactList.emptyConversationsTitle") }}</div>
                    <div class="empty-conversations-content-description">
                        {{ $t("chat.gridContactList.emptyConversationsDescription") }}
                    </div>
                    <div class="empty-conversations-content-action">
                        <SupsisButton @click="forwardCustomerUrl" primary
                            >{{ $t("message.customerPage") }}
                        </SupsisButton>
                    </div>
                </div>
            </div>
            <div class="loading-dots-container" v-if="loading">
                <div class="loading-dots">
                    <div class="dot"></div>
                    <div class="dot"></div>
                    <div class="dot"></div>
                </div>
            </div>
        </div>
        
        <ContextMenu
            :visible="contextMenu.visible"
            :x="contextMenu.x"
            :y="contextMenu.y"
            :items="contextMenuItems"
            @item-click="onContextMenuItemClick"
        />
    </ResizableContent>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import _ from "lodash";
import GridContactListItem from "./GridContactListItem.vue";
import ResizableContent from "./ResizableContent.vue";
import SupsisButton from "../../../views/Pages/Settings/components/SupsisButton.vue";
import EmptyChatList from "./svg/EmptyChatList.vue";
import log2 from "../../../utils/log2";
import AgentAvatar from "../../common/AgentAvatar.vue";
import GridContactListToolBar from "./GridContactListToolBar.vue";
import ContextMenu from "../../common/ContextMenu.vue";

const log = log2("GridContactList");

export default {
    name: "GridContactList",
    components: {
        AgentAvatar,
        GridContactListToolBar,
        EmptyChatList,
        SupsisButton,
        ResizableContent,
        GridContactListItem,
        ContextMenu,
    },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        contacts: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        maxGridConversationsWidth: {
            type: Number,
            default: 0,
        },
        minGridConversationsWidth: {
            type: Number,
            default: 0,
        },
    },
    computed: {
        ...mapGetters({}),
        ...mapState({
            selectedSortField: (state) => state.contact.selectedSortField,
            user: (state) => state.auth.user,
            visitorScreenUrl: (state) => {
                const isProduction = process.env.ENVIRONMENT === "production";
                const isStage = process.env.ENVIRONMENT === "stage";
                let siteAlias = (state.site.site.customerClientAddress || "").replace(".visitor.supsis.live", "");
                const protocol = isProduction || isStage ? "https" : "http";
                if (siteAlias === "visitor.supsis.live") siteAlias = "demo-site.supsis.live?customer=supsis";
                else siteAlias = "demo-site.supsis.live?customer=" + siteAlias;
                const hostname = isProduction
                    ? siteAlias
                    : isStage
                    ? `demo-site.supsis.live?url=${protocol}://${
                          state.site.site.customerClientAddress || "betavisitor.supsis.live"
                      }/static/js/loader.js`
                    : "localsite.supsis.live?customer=local";
                return `${protocol}://${hostname}`;
            },
        }),
        canToggleAnswerStatus() {
            if (!this.selectedContact) return false;
            const isDestroyed = _.get(this.selectedContact, "lastConversation.status") === "destroyed";
            return isDestroyed;
        },
        answerStatusButtonText() {
            if (!this.selectedContact) return "";
            return this.selectedContact.answerStatus === "answered" 
                ? this.$t("chat.gridContactList.markAsUnanswered")
                : this.$t("chat.gridContactList.markAsAnswered");
        },
        answerStatusIcon() {
            if (!this.selectedContact) return "";
            return this.selectedContact.answerStatus === "answered" ? "close" : "done";
        },
        canCloseChat() {
            if (!this.selectedContact) return false;
            return this.selectedContact?.lastConversation?.status !== "destroyed";
        },
        contextMenuItems() {
            const items = [];
            
            if (this.canToggleAnswerStatus) {
                items.push({
                    icon: this.answerStatusIcon,
                    label: this.answerStatusButtonText,
                    action: 'toggle-answer-status'
                });
            }
            
            if (this.selectedContact) {
                items.push({
                    icon: this.selectedContact.vipCustomer ? 'star_border' : 'star',
                    label: this.selectedContact.vipCustomer ? this.$t("chat.gridContactList.removeVIP") : this.$t("chat.gridContactList.markAsVIP"),
                    action: 'toggle-vip'
                });
                
                items.push({
                    icon: this.selectedContact.unread ? 'mark_email_read' : 'mark_email_unread',
                    label: this.selectedContact.unread ? this.$t("chat.gridContactList.markAsRead") : this.$t("chat.gridContactList.markAsUnread"),
                    action: 'toggle-unread'
                });
            }
            
            if (this.canCloseChat) {
                items.push({ divider: true });
                items.push({
                    icon: 'power_settings_new',
                    label: this.$t("chat.gridContactList.closeChat"),
                    action: 'close-chat',
                    danger: true
                });
            }
            
            return items;
        },
    },
    data: function () {
        return {
            width: 400,
            currentTime: Date.now(),
            timeUpdateInterval: null,
            isAutoLoading: false,
            contextMenu: {
                visible: false,
                x: 0,
                y: 0,
            },
            selectedContact: null,
            processingAction: false,
        };
    },

    created() {
        this.timeUpdateInterval = setInterval(() => {
            this.currentTime = Date.now();
        }, 1000);
    },
    mounted() {
        window.addEventListener("keydown", this.handleKeyboardShortcuts);
        window.addEventListener("click", this.closeContextMenu);
        this.checkAndLoadMoreIfNeeded();
    },
    updated() {
        this.checkAndLoadMoreIfNeeded();
    },
    methods: {
        ...mapActions({
            getContactById: "contact/getContactById",
            setSelectedContact: "contact/setSelectedContact",
            fetchContact: "contact/fetchContact",
            destroyContactConversation: "chat/destroyContactConversation",
            updateContactFields: "contact/updateContactFields",
            updateVisitorAttr: "visitor/updateVisitorAttr",
        }),
        onShowContextMenu({ x, y, contact }) {
            this.selectedContact = contact;
            this.contextMenu.visible = true;
            this.contextMenu.x = x;
            this.contextMenu.y = y;
        },
        closeContextMenu() {
            this.contextMenu.visible = false;
        },
        async onContextMenuItemClick(item) {
            if (this.processingAction) return;
            
            this.closeContextMenu();
            
            switch (item.action) {
                case 'toggle-answer-status':
                    await this.toggleAnswerStatus();
                    break;
                case 'toggle-vip':
                    await this.toggleVIP();
                    break;
                case 'toggle-unread':
                    await this.toggleUnread();
                    break;
                case 'close-chat':
                    await this.closeChat();
                    break;
            }
        },
        async toggleAnswerStatus() {
            if (!this.canToggleAnswerStatus || this.processingAction || !this.selectedContact) return;

            const newStatus = this.selectedContact.answerStatus === "answered" ? "unanswered" : "answered";
            const successMessage = newStatus === "answered" 
                ? this.$t("chat.gridContactList.markedAsAnswered")
                : this.$t("chat.gridContactList.markedAsUnanswered");

            try {
                this.processingAction = true;
                
                this.updateVisitorAttr({
                    publishSocket: false,
                    visitorId: this.selectedContact._id,
                    payload: {
                        answerStatus: newStatus,
                    },
                });

                this.updateContactFields({
                    contactId: this.selectedContact._id,
                    set: {
                        answerStatus: newStatus,
                    },
                });

                this.$message.success(successMessage);
            } catch (error) {
                this.$message.error(this.$t("chat.gridContactList.operationFailed"));
                log.error("toggleAnswerStatus error", error);
            } finally {
                this.processingAction = false;
            }
        },
        async toggleVIP() {
            if (this.processingAction || !this.selectedContact) return;

            const newVipStatus = !this.selectedContact.vipCustomer;
            const successMessage = newVipStatus 
                ? this.$t("chat.gridContactList.markedAsVIP")
                : this.$t("chat.gridContactList.vipRemoved");

            try {
                this.processingAction = true;
                
                this.updateVisitorAttr({
                    visitorId: this.selectedContact._id,
                    payload: {
                        vipCustomer: newVipStatus,
                    },
                });
                
                this.updateContactFields({
                    contactId: this.selectedContact._id,
                    set: {
                        vipCustomer: newVipStatus,
                    },
                });

                this.$message.success(successMessage);
            } catch (error) {
                this.$message.error(this.$t("chat.gridContactList.operationFailed"));
                log.error("toggleVIP error", error);
            } finally {
                this.processingAction = false;
            }
        },
        async toggleUnread() {
            if (this.processingAction || !this.selectedContact) return;

            const newUnreadStatus = !this.selectedContact.unread;
            const successMessage = newUnreadStatus 
                ? this.$t("chat.gridContactList.markedAsUnread")
                : this.$t("chat.gridContactList.markedAsRead");

            try {
                this.processingAction = true;
                
                this.updateVisitorAttr({
                    visitorId: this.selectedContact._id,
                    payload: {
                        unread: newUnreadStatus,
                    },
                });

                this.updateContactFields({
                    contactId: this.selectedContact._id,
                    set: {
                        unread: newUnreadStatus,
                    },
                });

                this.$message.success(successMessage);
            } catch (error) {
                this.$message.error(this.$t("chat.gridContactList.operationFailed"));
                log.error("toggleUnread error", error);
            } finally {
                this.processingAction = false;
            }
        },
        async closeChat() {
            if (!this.canCloseChat || this.processingAction || !this.selectedContact) return;

            this.$dialog
                .confirm(this.$t("chat.gridContactList.confirmCloseChat"), {
                    okText: this.$t("common.yes"),
                    cancelText: this.$t("common.cancel"),
                })
                .then(async () => {
                    try {
                        this.processingAction = true;
                        await this.destroyContactConversation(this.selectedContact);
                        this.$message.success(this.$t("chat.gridContactList.chatClosed"));
                    } catch (error) {
                        this.$message.error(this.$t("chat.gridContactList.operationFailed"));
                        log.error("closeChat error", error);
                    } finally {
                        this.processingAction = false;
                    }
                })
                .catch(() => {
                    // Dialog cancelled
                });
        },
        forwardCustomerUrl() {
            window.open(this.visitorScreenUrl, "_blank");
        },
        isActive(contact) {
            return this.contact && this.contact._id === contact._id;
        },
        isAssignToMe(contact) {
            return contact.lastConversation.users.includes(this.user._id);
        },
        async joinConversation(contactId) {
            let contact = await this.getContactById(contactId);
            if (!contact?.inited)
                contact = await this.fetchContact({
                    contactId,
                    ignoreSource: "GridContactList.joinConversation",
                    viewed: true,
                });
            this.setSelectedContact(contact);
        },
        onResizeConversations(handle, x, y, width) {
            width = Math.max(width, this.minGridConversationsWidth);
            if (width !== this.width) this.width = width;
        },
        scrollListener: _.debounce(function (e) {
            if (this.loading) {
                return;
            }
            
            const el = e.target;
            const scrollWhere = el.scrollTop + el.clientHeight + 5;
            if (scrollWhere >= el.scrollHeight) {
                this.$emit("loadMoreContacts");
            }
        }, 100),
        handleKeyboardShortcuts(e) {
            if (!e.altKey || this.contacts.length === 0) return;

            const currentIndex = this.contacts.findIndex((c) => c._id === this.contact?._id);

            let targetContact = null;

            if (e.code === "KeyJ") {
                e.preventDefault();
                if (currentIndex === -1) {
                    targetContact = this.contacts[0];
                } else if (currentIndex < this.contacts.length - 1) {
                    targetContact = this.contacts[currentIndex + 1];
                }
            } else if (e.code === "KeyK") {
                e.preventDefault();
                if (currentIndex === -1) {
                    targetContact = this.contacts[0];
                } else if (currentIndex > 0) {
                    targetContact = this.contacts[currentIndex - 1];
                }
            }

            if (targetContact) {
                this.joinConversation(targetContact._id);
                this.scrollToContact(targetContact._id);
            }
        },
        scrollToContact(contactId) {
            this.$nextTick(() => {
                const contactRef = this.$refs[`contact-${contactId}`];
                if (contactRef && contactRef[0]) {
                    contactRef[0].$el.scrollIntoView({
                        behavior: "smooth",
                        block: "nearest",
                    });
                }
            });
        },
        checkAndLoadMoreIfNeeded() {
            this.$nextTick(() => {
                const listElement = this.$refs.conversationsList;
                if (!listElement) return;

                // Eğer loading devam ediyorsa veya zaten otomatik yükleme yapılıyorsa, tekrar yükleme yapma
                if (this.loading || this.isAutoLoading) {
                    return;
                }

                const hasScroll = listElement.scrollHeight > listElement.clientHeight;
                
                // Scroll yoksa ve contact varsa, otomatik olarak daha fazla veri yükle
                if (!hasScroll && this.contacts.length > 0) {
                    this.isAutoLoading = true;
                    this.$emit("loadMoreContacts");
                }
            });
        },
    },
    beforeDestroy() {
        window.removeEventListener("keydown", this.handleKeyboardShortcuts);
        window.removeEventListener("click", this.closeContextMenu);
        if (this.timeUpdateInterval) {
            clearInterval(this.timeUpdateInterval);
            this.timeUpdateInterval = null;
        }
    },
    watch: {
        width() {
            this.$emit("width", this.width);
            localStorage.setItem("grid-conversation-width", this.width);
        },
        maxGridConversationsWidth() {
            const width = parseInt(
                localStorage.getItem("grid-conversation-width") || `${this.maxGridConversationsWidth * 0.5},`
            );
            this.width = Math.max(width, this.minGridConversationsWidth);
        },
        contacts: {
            handler() {
                // Contacts değiştiğinde scroll kontrolü yap ve gerekirse otomatik yükleme yap
                this.checkAndLoadMoreIfNeeded();
            },
            deep: true,
            immediate: true,
        },
        loading(newLoading, oldLoading) {
            // Loading bittiğinde otomatik yükleme flag'ini sıfırla ve tekrar kontrol et
            if (oldLoading && !newLoading) {
                this.isAutoLoading = false;
                this.checkAndLoadMoreIfNeeded();
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.conversations {
    width: 100%;
    height: 100% !important;
    border-right: 1px solid var(--color-border-default);
    display: flex;
    flex-direction: column;
    gap: 2px;
    position: relative;

    .conversation-lists {
        position: relative;
        overflow-x: hidden;
        overflow-y: scroll;
        width: 100%;
        height: auto;
        height: 100%;
        padding-left: 10px;
        padding-bottom: 80px;
        display: flex;
        flex-direction: column;

        &::-webkit-scrollbar {
            opacity: 0 !important;
            width: 10px !important;
            background: transparent !important;
        }

        &::-webkit-scrollbar-track {
            background: transparent !important;
        }

        &::-webkit-scrollbar-thumb {
            background: #22272d;
            background-clip: content-box !important;
            border: 4px solid transparent !important;
            border-radius: 10px !important;
        }
    }

    .loading-dots-container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        margin-top: 20px;
    }

    .loading-dots {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 8px 16px;
        background: rgba(255, 255, 255, 0.9);
        border-radius: 20px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        backdrop-filter: blur(4px);
        z-index: 100;
        width: fit-content;

        .dot {
            width: 6px;
            height: 6px;
            background-color: #22272d;
            border-radius: 50%;
            animation: dot-bounce 1.4s infinite ease-in-out;

            &:nth-child(1) {
                animation-delay: -0.32s;
            }

            &:nth-child(2) {
                animation-delay: -0.16s;
            }

            &:nth-child(3) {
                animation-delay: 0s;
            }
        }
    }

    @keyframes dot-bounce {
        0%,
        80%,
        100% {
            transform: scale(0.8);
            opacity: 0.5;
        }

        40% {
            transform: scale(1.2);
            opacity: 1;
        }
    }

    .empty-conversations {
        display: flex;
        width: 100%;
        height: 100%;
        align-items: center;
        justify-content: center;
        user-select: none;
        padding: 40px 20px;

        &-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            gap: 16px;
            max-width: 400px;

            svg {
                margin-bottom: 8px;
                animation: float 3s ease-in-out infinite;
            }

            &-title {
                font-size: 20px;
                font-weight: 600;
                color: #22272d;
                margin-top: 4px;
                letter-spacing: -0.02em;
            }

            &-description {
                font-size: 14px;
                line-height: 1.6;
                color: #6b7785;
                max-width: 320px;
            }

            &-action {
                margin-top: 8px;

                .supsis-button {
                    border-radius: 8px;
                    padding: 10px 24px;
                    font-weight: 600;
                    font-size: 14px;
                    background: transparent !important;
                    color: #6c727d !important;
                    border: 1.5px solid #d0d7de !important;
                    box-shadow: none !important;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

                    &:hover {
                        border-color: #6c727d !important;
                        color: #22272d !important;
                        background: #f8f9fa !important;
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px rgba(108, 114, 125, 0.15) !important;
                    }

                    &:active {
                        transform: translateY(0);
                        box-shadow: 0 2px 6px rgba(108, 114, 125, 0.1) !important;
                    }
                }
            }
        }
    }

    @keyframes float {
        0%, 100% {
            transform: translateY(0px);
        }
        50% {
            transform: translateY(-10px);
        }
    }

}
</style>
